import type {
  AttackProfile,
  AttackRange,
  RankUpgrades,
  Skill,
  SkillAdvancementBehaviour,
  SkillDetails,
  SkillKind,
  SkillTrait,
  SupportSpellEffectContent,
  WeaponUse,
} from '../content/types.ts';
import type { StatId } from '../domain/stats.ts';
import type { DamageType } from './damage.ts';
import {
  CUSTOM_SKILL_ID_PREFIX,
  MAX_CUSTOM_SKILL_DESCRIPTION_LENGTH,
  createCustomSkillLibrary,
  isCustomSkillId,
  validateCustomSkillLibrary,
} from '../domain/customSkill.ts';

/**
 * Explicit executable Attack mechanics for a custom Skill.
 *
 * There is deliberately no name/tag/prose inference here. Presence means the
 * author asked for Attack automation; absence means the Skill is represented
 * manually. `attackType` comes from the Skill kind and Spell Mana comes from
 * the Skill's explicit `manaCost`, keeping one authority for each fact.
 */
export interface CustomAttackDraft {
  toHitStat: StatId;
  baseDamage: {
    dice: string;
    stat: StatId | null;
    type: DamageType;
  };
  range: AttackRange | null;
  aiFavor: { amount: number; condition: string | null } | null;
  limitations: string[];
  cooldown: string | null;
  area: { kind: string; feet: number } | null;
  rank5: { addedDamageDice: string | null; notes: string[] };
  /** Required-and-nullable. Null explicitly means this is not a held weapon. */
  weaponUse: WeaponUse;
}

/**
 * The canonical definition draft for every player-authored Skill/Spell.
 *
 * Most fields are optional only so the legacy Utility/Passive screen can keep
 * sending the small draft it already knows how to edit. The builder supplies
 * the same historical defaults in that case. Rich callers can author every E7
 * representation field explicitly. Character Rank is intentionally absent.
 */
export interface CustomSkillDraft {
  name: string;
  kind: SkillKind;
  commonStat: StatId | null;
  description: string;
  traits?: SkillTrait[];
  advancement?: SkillAdvancementBehaviour;
  advancementNote?: string | null;
  manaCost?: number | null;
  maxRank?: number;
  supportEffect?: SupportSpellEffectContent;
  rankUpgrades?: RankUpgrades;
  notes?: string;
  details?: SkillDetails;
  /** Optional Attack automation. Missing/null means manual representation. */
  attack?: CustomAttackDraft | null;
}

export interface CustomSkillProblem {
  field:
    | 'name'
    | 'kind'
    | 'commonStat'
    | 'traits'
    | 'advancement'
    | 'advancementNote'
    | 'manaCost'
    | 'maxRank'
    | 'supportEffect'
    | 'description'
    | 'rankUpgrades'
    | 'notes'
    | 'details'
    | 'attack'
    | 'id';
  message: string;
}

export interface CustomSkillDefinition {
  skill: Skill;
  attack: AttackProfile | null;
}

/** Names are compared with case and surrounding space ignored. */
function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export const MAX_CUSTOM_SKILL_NAME = 60;
export const MAX_CUSTOM_SKILL_DESCRIPTION = MAX_CUSTOM_SKILL_DESCRIPTION_LENGTH;

function defaultTraits(kind: SkillKind): SkillTrait[] {
  return kind === 'passive' ? ['passive'] : [];
}

function defaultAdvancement(kind: SkillKind): SkillAdvancementBehaviour {
  return kind === 'passive' ? 'never-through-use' : 'normal-check-use';
}

function attackTypeFor(kind: SkillKind): AttackProfile['attackType'] | null {
  switch (kind) {
    case 'melee-attack':
      return 'melee';
    case 'ranged-attack':
      return 'ranged';
    case 'spell':
      return 'spell';
    case 'utility':
    case 'passive':
      return null;
  }
}

/**
 * Builds the complete reusable definition. No crawler state is read or written.
 * The caller supplies identity/timestamps; service code owns those values.
 */
export function buildCustomSkillDefinition(
  draft: CustomSkillDraft,
  id: string,
  skillCreatedAt: string,
  attackCreatedAt: string = skillCreatedAt,
): CustomSkillDefinition {
  if (!id.startsWith(CUSTOM_SKILL_ID_PREFIX)) {
    throw new Error(`A custom Skill ID must begin with "${CUSTOM_SKILL_ID_PREFIX}": got "${id}"`);
  }

  const traits = [...(draft.traits ?? defaultTraits(draft.kind))];
  const description = draft.description.trim();
  const skill: Skill = {
    id,
    name: draft.name.trim(),
    kind: draft.kind,
    // Preserve the old Passive-screen behaviour while leaving rich non-Passive
    // definitions exactly as authored.
    commonStat: draft.kind === 'passive' ? null : draft.commonStat,
    traits,
    advancement: draft.advancement ?? defaultAdvancement(draft.kind),
    advancementNote: draft.advancementNote ?? null,
    // null has one meaning only: this is not a Spell. A Spell draft that omits
    // manaCost remains invalid and is rejected by the shared library validator.
    manaCost: draft.kind === 'spell' ? (draft.manaCost ?? null) : (draft.manaCost ?? null),
    restriction: null,
    ...(draft.maxRank === undefined ? {} : { maxRank: draft.maxRank }),
    ...(draft.supportEffect === undefined ? {} : { supportEffect: { ...draft.supportEffect } }),
    ...(draft.rankUpgrades === undefined
      ? {}
      : { rankUpgrades: { ...draft.rankUpgrades } }),
    ...(draft.notes === undefined ? {} : { notes: draft.notes }),
    ...(draft.details === undefined
      ? {}
      : {
          details: {
            ...draft.details,
            ...(draft.details.tags === undefined ? {} : { tags: [...draft.details.tags] }),
          },
        }),
    ...(description === '' ? {} : { description }),
    source: { book: 'custom', createdAt: skillCreatedAt },
  };

  if (draft.attack === undefined || draft.attack === null) return { skill, attack: null };

  const attackType = attackTypeFor(draft.kind);
  if (attackType === null) {
    throw new Error(`A ${draft.kind} Skill cannot carry Attack automation.`);
  }

  const attack: AttackProfile = {
    id,
    attackType,
    toHitStat: draft.attack.toHitStat,
    baseDamage: { ...draft.attack.baseDamage },
    range: draft.attack.range === null ? null : { ...draft.attack.range },
    aiFavor: draft.attack.aiFavor === null ? null : { ...draft.attack.aiFavor },
    limitations: [...draft.attack.limitations],
    cooldown: draft.attack.cooldown,
    manaCost: attackType === 'spell' ? (skill.manaCost ?? null) : null,
    area: draft.attack.area === null ? null : { ...draft.attack.area },
    rank5: {
      addedDamageDice: draft.attack.rank5.addedDamageDice,
      notes: [...draft.attack.rank5.notes],
    },
    weaponUse: draft.attack.weaponUse === null ? null : { ...draft.attack.weaponUse },
    source: { book: 'custom', createdAt: attackCreatedAt },
  };

  return { skill, attack };
}

/**
 * The old call site only needs the Skill half. It still goes through the same
 * canonical builder, so there is no second rule set for Utility/Passive Skills.
 */
export function buildCustomSkill(draft: CustomSkillDraft, id: string, now: string): Skill {
  return buildCustomSkillDefinition(draft, id, now).skill;
}

function fieldForLibraryProblem(problem: string): CustomSkillProblem['field'] {
  if (problem.startsWith('attacks[')) return 'attack';
  if (problem.includes('Mana')) return 'manaCost';
  if (problem.includes('max Rank')) return 'maxRank';
  if (problem.includes('support effect')) return 'supportEffect';
  if (problem.includes('rankUpgrades')) return 'rankUpgrades';
  if (problem.includes(' details')) return 'details';
  if (problem.includes(' notes')) return 'notes';
  if (problem.includes('traits') || problem.includes('Passive Skill')) return 'traits';
  if (problem.includes('advance')) return 'advancement';
  if (problem.includes('advancement note')) return 'advancementNote';
  if (problem.includes('Common Stat') || problem.includes('Skill Check')) return 'commonStat';
  if (problem.includes('description')) return 'description';
  if (problem.includes('must be one of')) return 'kind';
  return 'kind';
}

/**
 * Checks a draft against the catalogue and then against the SAME schema-3
 * authority used for local persistence/import. This makes the editor/service a
 * producer of records the repository is guaranteed to accept rather than a
 * parallel validator that can drift.
 */
export function checkCustomSkillDraft(
  draft: CustomSkillDraft,
  existingSkills: readonly Skill[],
  editingId: string | null = null,
): CustomSkillProblem[] {
  const problems: CustomSkillProblem[] = [];
  const name = draft.name.trim();

  if (name === '') {
    problems.push({ field: 'name', message: 'A Skill needs a name.' });
  } else if (name.length > MAX_CUSTOM_SKILL_NAME) {
    problems.push({
      field: 'name',
      message: `A Skill name is at most ${MAX_CUSTOM_SKILL_NAME} characters.`,
    });
  } else {
    const clash = existingSkills.find(
      (s) => s.id !== editingId && normalizeName(s.name) === normalizeName(name),
    );
    if (clash) {
      problems.push({
        field: 'name',
        message: isCustomSkillId(clash.id)
          ? `You already have a custom Skill called ${clash.name}.`
          : `${clash.name} is already a Skill in the book. Pick a different name.`,
      });
    }
  }

  if (draft.description.length > MAX_CUSTOM_SKILL_DESCRIPTION) {
    problems.push({
      field: 'description',
      message: `A description is at most ${MAX_CUSTOM_SKILL_DESCRIPTION} characters.`,
    });
  }

  if (draft.kind === 'passive' && draft.commonStat !== null) {
    problems.push({ field: 'commonStat', message: 'A Passive Skill must not have a Common Stat.' });
  }

  if (editingId !== null && !isCustomSkillId(editingId)) {
    problems.push({
      field: 'id',
      message: 'Only custom Skills can be edited. Skills from the book are fixed.',
    });
  }

  // Use a harmless preview identity. No timestamp/ID from this preview is ever
  // persisted; the service assigns the real ones only after validation passes.
  try {
    const definition = buildCustomSkillDefinition(
      draft,
      editingId ?? 'custom:draft-preview',
      '1970-01-01T00:00:00.000Z',
    );
    const library = createCustomSkillLibrary('1970-01-01T00:00:00.000Z');
    const check = validateCustomSkillLibrary({
      ...library,
      skills: [definition.skill],
      attacks: definition.attack === null ? [] : [definition.attack],
    });
    for (const message of check.problems) {
      // Name/description/id have friendlier checks above. Avoid duplicate noise.
      if (message.includes('has no name') || message.includes('description is longer')) continue;
      problems.push({ field: fieldForLibraryProblem(message), message });
    }
  } catch (err) {
    problems.push({
      field: 'attack',
      message: err instanceof Error ? err.message : String(err),
    });
  }

  return problems;
}

/**
 * Applies a definition edit while preserving stable identity and creation dates.
 * A newly-added profile gets the caller's `now`; an existing profile keeps its
 * own creation date.
 */
export function applyCustomSkillDefinitionEdit(
  existing: Skill,
  existingAttack: AttackProfile | null,
  draft: CustomSkillDraft,
  now: string,
): CustomSkillDefinition {
  const skillCreatedAt = existing.source.book === 'custom' ? existing.source.createdAt : now;
  const attackCreatedAt =
    existingAttack?.source.book === 'custom' ? existingAttack.source.createdAt : now;
  return buildCustomSkillDefinition(draft, existing.id, skillCreatedAt, attackCreatedAt);
}

/** Backwards-compatible Skill-only edit helper. */
export function applyCustomSkillEdit(existing: Skill, draft: CustomSkillDraft): Skill {
  const createdAt = existing.source.book === 'custom' ? existing.source.createdAt : '';
  return buildCustomSkillDefinition(draft, existing.id, createdAt).skill;
}

/**
 * A rich draft reconstructed from reusable content. No crawler Rank can enter
 * this function because it accepts only definition objects.
 */
export function definitionDraftFromSkill(
  skill: Skill,
  attack: AttackProfile | null = null,
): CustomSkillDraft {
  return {
    name: skill.name,
    kind: skill.kind,
    commonStat: skill.commonStat,
    description: skill.description ?? '',
    traits: [...skill.traits],
    advancement: skill.advancement,
    advancementNote: skill.advancementNote,
    manaCost: skill.manaCost,
    ...(skill.maxRank === undefined ? {} : { maxRank: skill.maxRank }),
    ...(skill.supportEffect === undefined ? {} : { supportEffect: { ...skill.supportEffect } }),
    ...(skill.rankUpgrades === undefined ? {} : { rankUpgrades: { ...skill.rankUpgrades } }),
    ...(skill.notes === undefined ? {} : { notes: skill.notes }),
    ...(skill.details === undefined
      ? {}
      : {
          details: {
            ...skill.details,
            ...(skill.details.tags === undefined ? {} : { tags: [...skill.details.tags] }),
          },
        }),
    ...(attack === null
      ? { attack: null }
      : {
          attack: {
            toHitStat: attack.toHitStat,
            baseDamage: {
              dice: attack.baseDamage.dice,
              stat: attack.baseDamage.stat,
              type: attack.baseDamage.type as DamageType,
            },
            range: attack.range === null ? null : { ...attack.range },
            aiFavor: attack.aiFavor === null ? null : { ...attack.aiFavor },
            limitations: [...attack.limitations],
            cooldown: attack.cooldown,
            area: attack.area === null ? null : { ...attack.area },
            rank5: {
              addedDamageDice: attack.rank5.addedDamageDice,
              notes: [...attack.rank5.notes],
            },
            weaponUse: attack.weaponUse === null ? null : { ...attack.weaponUse },
          },
        }),
  };
}

/**
 * Legacy Utility/Passive form adapter. It is intentionally only safe for those
 * two kinds; the E7c editor will use `definitionDraftFromSkill` directly.
 */
export function isLegacyEditableCustomSkill(skill: Skill): boolean {
  const expectedTraits = defaultTraits(skill.kind);
  const expectedAdvancement = defaultAdvancement(skill.kind);
  return (
    (skill.kind === 'utility' || skill.kind === 'passive') &&
    skill.traits.length === expectedTraits.length &&
    skill.traits.every((trait, index) => trait === expectedTraits[index]) &&
    skill.advancement === expectedAdvancement &&
    skill.advancementNote === null &&
    skill.manaCost === null &&
    skill.maxRank === undefined &&
    skill.supportEffect === undefined &&
    skill.rankUpgrades === undefined &&
    skill.notes === undefined &&
    skill.details === undefined &&
    skill.restriction === null
  );
}

export function draftFromCustomSkill(skill: Skill): CustomSkillDraft {
  if (!isLegacyEditableCustomSkill(skill)) {
    throw new Error('The legacy custom Skill editor cannot faithfully represent this definition.');
  }
  return {
    name: skill.name,
    kind: skill.kind,
    commonStat: skill.commonStat,
    description: skill.description ?? '',
  };
}
